import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  Building2, CreditCard, FileText, CheckCircle, ArrowRight, 
  Calculator, BookOpen, AlertCircle, ChevronRight, Euro
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const ChargesCopropriete = () => {
  const tableOfContents = [
    { id: "definition", title: "Que sont les charges de copropriété ?" },
    { id: "types", title: "Types de charges" },
    { id: "repartition", title: "Répartition des charges" },
    { id: "appel-fonds", title: "L'appel de fonds" },
    { id: "impayes", title: "Gestion des impayés" },
    { id: "contestation", title: "Contestation des charges" },
    { id: "reduction", title: "Réduire ses charges" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Charges de Copropriété : Guide Complet 2025 | Répartition, calcul, impayés</title>
        <meta name="description" content="Tout savoir sur les charges de copropriété : répartition selon les tantièmes, appels de fonds, gestion des impayés, contestation. Guide complet pour les copropriétaires." />
        <meta name="keywords" content="charges copropriété, appel de fonds, tantièmes copropriété, charges communes, charges spéciales, impayés copropriété, répartition charges" />
        <link rel="canonical" href="https://copropriete.app/charges-copropriete" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>
                Se connecter
              </Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                C'est gratuit
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Guide complet 2025
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">
              Charges de Copropriété
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprendre, calculer et optimiser les charges de votre copropriété. Guide complet pour tous les copropriétaires.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-24 bg-slate-50 rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-4">Sommaire</h3>
              <nav className="space-y-2">
                {tableOfContents.map((item) => (
                  <a key={item.id} href={`#${item.id}`} className="flex items-center gap-2 text-slate-600 hover:text-orange-600 transition-colors text-sm py-1">
                    <ChevronRight className="w-4 h-4" />
                    {item.title}
                  </a>
                ))}
              </nav>
              
              <div className="mt-8 p-4 bg-orange-100 rounded-xl">
                <p className="text-sm text-orange-800 font-medium mb-3">
                  Gérez vos appels de fonds facilement
                </p>
                <Button size="sm" onClick={() => window.location.href = '/login'} className="w-full bg-orange-600 hover:bg-orange-700">
                  C'est gratuit 7 jours
                </Button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 prose prose-slate prose-lg max-w-none">
            <section id="definition" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Euro className="w-8 h-8 text-orange-600" />
                Que sont les charges de copropriété ?
              </h2>
              <p>
                Les <strong>charges de copropriété</strong> sont les dépenses nécessaires au fonctionnement et à l'entretien des parties communes d'un immeuble. Elles sont réparties entre tous les copropriétaires selon des règles définies dans le règlement de copropriété.
              </p>
              <p>
                Ces charges couvrent l'ensemble des frais liés à :
              </p>
              <ul>
                <li>L'entretien courant de l'immeuble (ménage, petites réparations)</li>
                <li>Les équipements communs (ascenseur, chauffage collectif)</li>
                <li>Les assurances de l'immeuble</li>
                <li>Les honoraires du syndic</li>
                <li>Les travaux de conservation et d'amélioration</li>
              </ul>
            </section>

            <section id="types" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-orange-600" />
                Types de charges
              </h2>
              
              <h3 className="text-xl font-bold text-slate-800">Charges générales (Article 10 al. 2)</h3>
              <p>
                Les charges générales concernent la <strong>conservation, l'entretien et l'administration</strong> des parties communes. Elles sont réparties en fonction des tantièmes généraux de chaque lot.
              </p>
              <ul>
                <li>Entretien des parties communes (hall, escaliers, jardin)</li>
                <li>Assurance de l'immeuble</li>
                <li>Honoraires du syndic</li>
                <li>Frais de gestion administrative</li>
                <li>Petites réparations</li>
              </ul>

              <h3 className="text-xl font-bold text-slate-800">Charges spéciales (Article 10 al. 1)</h3>
              <p>
                Les charges spéciales concernent les <strong>services collectifs et équipements communs</strong>. Elles sont réparties en fonction de l'utilité que chaque lot retire de ces services.
              </p>
              <ul>
                <li>Ascenseur (réparti selon l'étage)</li>
                <li>Chauffage collectif</li>
                <li>Eau froide/chaude collective</li>
                <li>Antenne collective</li>
                <li>Gardiennage</li>
              </ul>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl my-8">
                <p className="text-orange-900 font-medium m-0">
                  💡 <strong>Exemple :</strong> Un appartement au rez-de-chaussée ne paie généralement pas les charges d'ascenseur, car il n'en retire aucune utilité.
                </p>
              </div>
            </section>

            <section id="repartition" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-orange-600" />
                Répartition des charges
              </h2>
              
              <h3 className="text-xl font-bold text-slate-800">Les tantièmes</h3>
              <p>
                La répartition des charges est basée sur les <strong>tantièmes</strong>, une quote-part exprimée en millièmes ou dix-millièmes, attribuée à chaque lot. Les tantièmes sont calculés en fonction de :
              </p>
              <ul>
                <li>La superficie du lot</li>
                <li>Sa situation dans l'immeuble (étage, exposition)</li>
                <li>Sa consistance (appartement, cave, parking)</li>
              </ul>

              <h3 className="text-xl font-bold text-slate-800">Exemple de calcul</h3>
              <div className="bg-slate-100 rounded-xl p-6 my-6">
                <p className="font-mono text-sm">
                  Budget annuel total : 50 000 €<br />
                  Vos tantièmes : 150/1000<br /><br />
                  <strong>Votre quote-part = 50 000 € × (150/1000) = 7 500 € / an</strong>
                </p>
              </div>
            </section>

            <section id="appel-fonds" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-orange-600" />
                L'appel de fonds
              </h2>
              <p>
                L'<strong>appel de fonds</strong> (ou appel de charges) est le document envoyé par le syndic pour demander le paiement des charges. Il peut être :
              </p>

              <h3 className="text-xl font-bold text-slate-800">Appel provisionnel</h3>
              <p>
                Basé sur le budget prévisionnel voté en AG, il est généralement trimestriel et payable le 1er jour de chaque trimestre.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Appel pour travaux</h3>
              <p>
                Pour financer des travaux votés en AG, avec un échéancier défini.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Régularisation</h3>
              <p>
                Après approbation des comptes, une régularisation ajuste les provisions versées aux dépenses réelles.
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl my-8">
                <p className="text-emerald-900 font-medium m-0">
                  ✅ <strong>Avec copropriete.app</strong>, créez et envoyez vos appels de fonds automatiquement, avec calcul instantané selon les tantièmes.
                </p>
              </div>
            </section>

            <section id="impayes" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-orange-600" />
                Gestion des impayés
              </h2>
              <p>
                Les impayés de charges sont un problème fréquent qui peut mettre en péril les finances de la copropriété.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Procédure de recouvrement</h3>
              <ol>
                <li><strong>Relance amiable</strong> - Courrier simple puis recommandé</li>
                <li><strong>Mise en demeure</strong> - Lettre recommandée avec AR</li>
                <li><strong>Injonction de payer</strong> - Procédure judiciaire simplifiée</li>
                <li><strong>Saisie</strong> - En cas de non-paiement persistant</li>
              </ol>

              <h3 className="text-xl font-bold text-slate-800">Privilège du syndicat</h3>
              <p>
                Le syndicat des copropriétaires bénéficie d'un <strong>privilège spécial immobilier</strong> (hypothèque légale) sur le lot du copropriétaire débiteur pour garantir le paiement des charges.
              </p>
            </section>

            <section id="contestation" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Contestation des charges
              </h2>
              <p>
                Un copropriétaire peut contester la répartition des charges s'il estime qu'elle est injuste ou non conforme au règlement de copropriété.
              </p>

              <h3 className="text-xl font-bold text-slate-800">Motifs de contestation</h3>
              <ul>
                <li>Erreur de calcul</li>
                <li>Non-conformité au règlement de copropriété</li>
                <li>Répartition déséquilibrée (écart de plus de 25%)</li>
                <li>Charges sans rapport avec l'utilité du lot</li>
              </ul>

              <h3 className="text-xl font-bold text-slate-800">Procédure</h3>
              <p>
                La contestation doit être portée devant le <strong>tribunal judiciaire</strong> dans un délai de 5 ans à compter de la publication du règlement de copropriété ou de la modification contestée.
              </p>
            </section>

            <section id="reduction" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-orange-600" />
                Réduire ses charges
              </h2>
              <p>
                Plusieurs leviers permettent de réduire les charges de copropriété :
              </p>
              <ul>
                <li><strong>Passer en syndic bénévole</strong> - Économie des honoraires de syndic</li>
                <li><strong>Renégocier les contrats</strong> - Assurance, entretien, énergie</li>
                <li><strong>Travaux d'économie d'énergie</strong> - Isolation, LED, régulation chauffage</li>
                <li><strong>Mise en concurrence</strong> - Pour tous les travaux et prestations</li>
                <li><strong>Individualisation des compteurs</strong> - Eau, chauffage</li>
              </ul>

              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 my-8 border border-orange-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Maîtrisez vos charges avec copropriete.app</h3>
                <p className="text-slate-700 mb-6">
                  Notre plateforme vous aide à suivre vos dépenses, anticiper votre budget et optimiser la gestion financière de votre copropriété.
                </p>
                <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-orange-600 hover:bg-orange-700">
                  Essayer gratuitement pendant 7 jours
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default ChargesCopropriete;
